import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClickAwayListener } from '@material-ui/core';
import cx from 'classnames';
import { lowerCase, upperFirst } from 'lodash';
import PropTypes from 'prop-types';

import { IconCloseThin } from 'src/assets/icons/CloseThin';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GENERATION_PANEL_MENU_TYPE } from 'src/const';
import { AI_SUGGESTION_MODE } from 'src/data/generation';
import { isNonEnglish } from 'src/data/generation';
import {
  getFormulaTitle,
  getToolDescription,
  getToolGenerationModes,
  isMixtureTool,
} from 'src/data/generationTool';
import { useListenToGenerationPanelMenuOpen, useMenuOpenedState } from 'src/lib/hooks';
import { useSetupMenuFloating } from 'src/lib/hooks/useSetupMenuFloating';
import pubsub, { PUBSUB } from 'src/lib/pubsub';
import { BlockCustomFormula } from 'src/views/BlockCustomFormula';

import { useStyles } from './useStyles';

export const FormulaMenu = ({
  className,
  onClose,
  isMenuOpen,
  referenceElement,
  isInfinityMode,
  generationTool,
  generationModeId,
  generationModeType,
  maxChars,
  casing,
  brandVoicesAssets,
  errors,
  onChangeFormula,
  onChangeMaxChars,
  onChangeCasing,
  subGenerationToolsProps,
  subGenerationTool,
  language,
}) => {
  const styles = useStyles();
  const [popperElement, setPopperElement] = useState(null);
  const { floatingStyles } = useSetupMenuFloating({
    popperElement,
    referenceElement,
    isInfinityMode,
  });

  const {
    menuOpened: casesMenuOpened,
    handleMenuOpen: handleCasesMenuOpen,
    handleMenuClose: handleCasesMenuClose,
  } = useMenuOpenedState();

  const selectedGenerationTool = isMixtureTool(generationTool) ? subGenerationTool : generationTool;

  const formulaTitle = getFormulaTitle(selectedGenerationTool);
  const isRecomposeTool = formulaTitle !== 'Formula';
  const customFormulaTitle = getToolDescription(selectedGenerationTool);
  const isEnglish = !isNonEnglish(language);

  const isCustomFormulasVisible = useMemo(() => {
    const availableTemplates = getToolGenerationModes(selectedGenerationTool);
    return availableTemplates.includes(AI_SUGGESTION_MODE.customFormula);
  }, [selectedGenerationTool]);

  const currentErrors = isMixtureTool(generationTool) ? subGenerationToolsProps?.errors : errors;
  const errorStyle = currentErrors?.errorAlert || currentErrors?.maxChars;

  useListenToGenerationPanelMenuOpen(GENERATION_PANEL_MENU_TYPE.formulas, onClose);

  useEffect(() => {
    if (isMenuOpen) {
      pubsub.publish(PUBSUB.OPEN_GENERATION_PANEL_MENU, {
        openedMenuType: GENERATION_PANEL_MENU_TYPE.formulas,
      });
    }
  }, [isMenuOpen]);

  if (!isMenuOpen) {
    return null;
  }

  return createPortal(
    <ClickAwayListener onClickAway={() => (casesMenuOpened ? null : onClose())}>
      <div
        className={cx(className, styles.root, {
          [styles.isInfinityMode]: isInfinityMode,
        })}
        ref={setPopperElement}
        style={floatingStyles}
      >
        <div className={styles.titleWrapper}>
          <CustomTypography
            className={styles.title}
            variant={TYPOGRAPHY_VARIANT.medium12}
          >
            <span>{isEnglish ? 'Formatting & formulas' : 'Formatting'}</span>
            {customFormulaTitle && <span>: {upperFirst(lowerCase(customFormulaTitle))}</span>}
          </CustomTypography>
          <IconCloseThin
            className={styles.closeButton}
            onClick={onClose}
          />
        </div>

        <BlockCustomFormula
          className={cx(styles.blockCustomFormula, { [styles.error]: errorStyle })}
          isRecomposeTool={isRecomposeTool}
          isCustomFormulasVisible={isCustomFormulasVisible}
          generationTool={generationTool}
          selectedModeId={generationModeId}
          selectedModeType={generationModeType}
          onSelect={onChangeFormula}
          maxChars={maxChars}
          onMaxCharsChange={onChangeMaxChars}
          brandVoicesAssets={brandVoicesAssets}
          errors={errors}
          casing={casing}
          onChangeCasing={onChangeCasing}
          displayMaxChars
          hideBrandVoiceSettings
          onMenuClose={onClose}
          onCasesMenuOpen={handleCasesMenuOpen}
          onCasesMenuClose={handleCasesMenuClose}
          subGenerationToolsProps={subGenerationToolsProps}
          subGenerationTool={subGenerationTool}
          language={language}
        />
      </div>
    </ClickAwayListener>,
    document.body
  );
};

FormulaMenu.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  isMenuOpen: PropTypes.bool,
  referenceElement: PropTypes.instanceOf(Element),
  isInfinityMode: PropTypes.bool,
  onChangeFormula: PropTypes.func,
  onChangeCasing: PropTypes.func,
  onChangeMaxChars: PropTypes.func,
  generationTool: PropTypes.string,
  generationModeId: PropTypes.string,
  generationModeType: PropTypes.string,
  maxChars: PropTypes.number,
  casing: PropTypes.string,
  subGenerationToolsProps: PropTypes.shape(),
  errors: PropTypes.shape(),
  brandVoicesAssets: PropTypes.arrayOf(PropTypes.shape()),
  subGenerationTool: PropTypes.string,
  language: PropTypes.string,
};
